import { useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PROMOTION_CAMPAIGN_ENABLED } from '../config/promotionCampaign';

export interface CatalogFilters {
  page: number;
  search: string;
  categoryIds: number[];
  collectionId: number | undefined;
  colorId: number | undefined;
  variationTypeId: number | undefined;
  variationValues: string[];
  gender: number | undefined;
  minPrice: number | undefined;
  maxPrice: number | undefined;
  hasPromotion: boolean;
  promotionLocked: boolean;
  selectedSortIndex: number;

  searchInput: string;
  minPriceInput: string;
  maxPriceInput: string;

  setPage: (page: number) => void;
  handleSearchChange: (value: string) => void;
  handleCategoryChange: (ids: number[]) => void;
  handleCollectionChange: (id: number | undefined) => void;
  handleColorChange: (id: number | undefined) => void;
  handleVariationTypeChange: (typeId: number | undefined) => void;
  handleVariationChange: (values: string[]) => void;
  handleCategoryFromSearch: (categoryIds: number[]) => void;
  handleVariationSelect: (typeId: number, values: string[]) => void;
  handleChildSizeSelect: (typeId: number, values: string[], gender: number | undefined) => void;
  clearChildSize: () => void;
  handleGenderChange: (value: number | undefined) => void;
  handlePromotionChange: (value: boolean) => void;
  handleCategoryAndGenderChange: (categoryIds: number[], gender: number | undefined) => void;
  handleMinPriceChange: (raw: string) => void;
  handleMaxPriceChange: (raw: string) => void;
  handleSortChange: (idx: number) => void;
  clearFilters: () => void;

  hasFilters: boolean;
}

export function useCatalogFilters(): CatalogFilters {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const search = searchParams.get('q') ?? '';
  const categoryIds = searchParams.get('cat')
    ? searchParams.get('cat')!.split(',').map(Number).filter((n) => !isNaN(n))
    : [];
  const collectionId = searchParams.get('col') !== null ? Number(searchParams.get('col')) : undefined;
  const colorId = searchParams.get('color') !== null ? Number(searchParams.get('color')) : undefined;
  const variationTypeId = searchParams.get('varType') !== null ? Number(searchParams.get('varType')) : undefined;
  const variationValues = searchParams.get('var')
    ? searchParams.get('var')!.split(',').filter((v) => v !== '')
    : [];
  const gender = searchParams.get('gender') !== null ? Number(searchParams.get('gender')) : undefined;
  const minPrice = searchParams.get('minP') !== null ? Number(searchParams.get('minP')) : undefined;
  const maxPrice = searchParams.get('maxP') !== null ? Number(searchParams.get('maxP')) : undefined;
  const hasPromotion = PROMOTION_CAMPAIGN_ENABLED ? true : searchParams.get('promo') === '1';
  const selectedSortIndex = parseInt(searchParams.get('sort') ?? '0', 10) || 0;

  const [searchInput, setSearchInput] = useState(search);
  const [minPriceInput, setMinPriceInput] = useState(minPrice !== undefined ? String(minPrice) : '');
  const [maxPriceInput, setMaxPriceInput] = useState(maxPrice !== undefined ? String(maxPrice) : '');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const debounce = (fn: () => void) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(fn, 400);
  };

  const buildParams = (overrides: Record<string, string | undefined>): URLSearchParams => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(overrides)) {
      if (value === undefined || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    return next;
  };

  const updateParams = (overrides: Record<string, string | undefined>) => {
    const next = buildParams({ ...overrides, page: undefined });
    setSearchParams(next, { replace: true });
  };

  const setPage = (newPage: number) => {
    const next = buildParams({ page: newPage === 1 ? undefined : String(newPage) });
    setSearchParams(next, { replace: true });
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debounce(() => updateParams({ q: value || undefined }));
  };

  const handleCategoryChange = (ids: number[]) => {
    updateParams({ cat: ids.length > 0 ? ids.join(',') : undefined });
  };

  const handleCollectionChange = (id: number | undefined) => {
    updateParams({ col: id !== undefined ? String(id) : undefined });
  };

  const handleColorChange = (id: number | undefined) => {
    updateParams({ color: id !== undefined ? String(id) : undefined });
  };

  const handleVariationTypeChange = (typeId: number | undefined) => {
    updateParams({ varType: typeId !== undefined ? String(typeId) : undefined, var: undefined });
  };

  const handleVariationChange = (values: string[]) => {
    updateParams({ var: values.length > 0 ? values.join(',') : undefined });
  };

  const handleVariationSelect = (typeId: number, values: string[]) => {
    updateParams({ varType: String(typeId), var: values.length > 0 ? values.join(',') : undefined });
  };

  const handleCategoryFromSearch = (ids: number[]) => {
    clearTimeout(debounceRef.current);
    setSearchInput('');
    updateParams({ cat: ids.length > 0 ? ids.join(',') : undefined, q: undefined });
  };

  const handleChildSizeSelect = (typeId: number, values: string[], childGender: number | undefined) => {
    updateParams({
      varType: String(typeId),
      var: values.length > 0 ? values.join(',') : undefined,
      gender: childGender !== undefined ? String(childGender) : undefined,
    });
  };

  const clearChildSize = () => {
    updateParams({ varType: undefined, var: undefined, gender: undefined });
  };

  const handleGenderChange = (value: number | undefined) => {
    updateParams({ gender: value !== undefined ? String(value) : undefined });
  };

  const handlePromotionChange = (value: boolean) => {
    if (PROMOTION_CAMPAIGN_ENABLED) return;
    updateParams({ promo: value ? '1' : undefined });
  };

  const handleCategoryAndGenderChange = (categoryIds: number[], gender: number | undefined) => {
    updateParams({
      cat: categoryIds.length > 0 ? categoryIds.join(',') : undefined,
      gender: gender !== undefined ? String(gender) : undefined,
    });
  };

  const handleMinPriceChange = (raw: string) => {
    setMinPriceInput(raw);
    const parsed = raw === '' ? undefined : Number(raw);
    const value = parsed !== undefined && !isNaN(parsed) ? String(parsed) : undefined;
    debounce(() => updateParams({ minP: value }));
  };

  const handleMaxPriceChange = (raw: string) => {
    setMaxPriceInput(raw);
    const parsed = raw === '' ? undefined : Number(raw);
    const value = parsed !== undefined && !isNaN(parsed) ? String(parsed) : undefined;
    debounce(() => updateParams({ maxP: value }));
  };

  const handleSortChange = (idx: number) => {
    updateParams({ sort: idx === 0 ? undefined : String(idx) });
  };

  const clearFilters = () => {
    setSearchInput('');
    setMinPriceInput('');
    setMaxPriceInput('');
    if (PROMOTION_CAMPAIGN_ENABLED && searchParams.get('promo') === '1') {
      setSearchParams(new URLSearchParams({ promo: '1' }));
    } else {
      setSearchParams({});
    }
  };

  const hasFilters = searchParams.size > 0;

  return {
    page,
    search,
    categoryIds,
    collectionId,
    colorId,
    variationTypeId,
    variationValues,
    gender,
    minPrice,
    maxPrice,
    hasPromotion,
    promotionLocked: PROMOTION_CAMPAIGN_ENABLED,
    selectedSortIndex,

    searchInput,
    minPriceInput,
    maxPriceInput,

    setPage,
    handleSearchChange,
    handleCategoryChange,
    handleCollectionChange,
    handleColorChange,
    handleVariationTypeChange,
    handleVariationChange,
    handleVariationSelect,
    handleChildSizeSelect,
    clearChildSize,
    handleCategoryFromSearch,
    handleGenderChange,
    handlePromotionChange,
    handleCategoryAndGenderChange,
    handleMinPriceChange,
    handleMaxPriceChange,
    handleSortChange,
    clearFilters,

    hasFilters,
  };
}
